/**
 * El comprobante electrónico pintado como documento, y el visor que lo escala.
 *
 * Vive acá y no en el historial fiscal del administrador porque **el mismo
 * documento lo ven dos personas distintas**: el dueño desde `Facturación`, y el
 * cliente desde su cobro en el portal. Es literalmente el papel que el cliente
 * ya recibió una vez por correo, así que las dos vistas tienen que ser el mismo
 * render — si se duplicara, tarde o temprano una de las dos empezaría a decir
 * algo distinto de la otra sobre un documento fiscal.
 *
 * La vista se arma leyendo el XML original, no una copia en la base: el
 * comprobante es el único original, y duplicarlo sería crear una segunda
 * verdad que puede divergir.
 *
 * Que sea derivada no la exime de ser **fiel**: reproduce el comprobante que
 * emitió el proveedor de facturación campo por campo, porque es el documento
 * que el cliente ya recibió una vez y espera reconocer. Lo único que no se
 * reproduce son tres cosas que GTI imprime y el XML no trae —el «Código Único
 * de Consulta», el «Destinatario» y el «Fax»—; el detalle, en `InvoiceDocument`.
 */

import { useLayoutEffect, useRef, useState } from 'react';
import {
  CONDICION_VENTA, TIPO_CODIGO_COMERCIAL, TIPO_COMPROBANTE,
  TIPO_IDENTIFICACION, TIPO_MEDIO_PAGO, cedulaLabel, fechaEmisionLabel,
  formatMoney, haciendaLabel, montoEnLetras,
} from '@cleanblue/shared';
import { qrSvg } from '@/lib/qr';
import '@/styles/invoice-doc.css';

/**
 * ¿Es una nota de crédito? En la base el tipo es el texto tal cual vino del
 * import («Nota de Crédito») — el literal 'NOTA_CREDITO' que se comparaba
 * antes no existe en ninguna de las 174 filas reales, así que el distintivo
 * nunca llegaba a pintarse. Se aceptan las dos formas por si un import futuro
 * normaliza el valor.
 */
export const isCreditNote = (documentType: string | null): boolean =>
  documentType != null
  && (documentType === 'NOTA_CREDITO' || /nota\s+de\s+cr(é|e)dito/i.test(documentType));

interface Party {
  legal_name: string | null; trade_name?: string | null;
  tax_id: string | null; tax_id_type?: string | null;
  email: string | null; phone: string | null; phone_country?: string | null;
  address: string | null; barrio?: string | null;
}

interface InvoiceLine {
  numero?: string | null;
  detalle: string | null; cantidad: string | null; unidad: string | null; cabys: string | null;
  codigo_comercial?: string | null; codigo_comercial_tipo?: string | null;
  precio_unitario: string | null; descuento: string | null; naturaleza_descuento?: string | null;
  subtotal: string | null; base_imponible?: string | null;
  impuesto: string | null; impuesto_codigo?: string | null;
  impuesto_tarifa_codigo?: string | null; impuesto_tarifa?: string | null;
  total_linea: string | null;
}

export interface InvoiceDetail {
  document_number: string;
  /**
   * El código del consultorio. El comprobante lo imprime como «Código Interno»
   * —es el código del cliente en el sistema del proveedor de facturación— y no
   * está en el XML, pero es el mismo `unit_code` que Clean Blue ya usa, así que
   * no hay nada que suponer.
   */
  unit_code?: string | null;
  tipo_comprobante?: string | null;
  version?: string | null;
  clave?: string | null;
  proveedor_sistemas?: string | null;
  codigo_actividad_emisor?: string | null;
  issued_on: string;
  condicion_venta?: string | null;
  medios_pago?: { tipo: string | null; total: string | null }[];
  notas?: string[];
  emisor?: Party | null;
  receptor?: Party | null;
  lines: InvoiceLine[];
  totals?: {
    moneda: string; tipo_cambio?: string | null;
    gravado?: string | null; exento?: string | null; exonerado?: string | null;
    venta?: string | null; descuentos?: string | null;
    venta_neta: string | null;
    desglose?: { codigo: string | null; tarifa_codigo: string | null; monto: string | null }[];
    impuesto: string | null; comprobante: string | null;
  } | null;
  firmado?: boolean;
  referencia?: {
    tipo_documento: string | null; clave: string | null; consecutivo: string | null;
    issued_on: string | null; codigo: string | null; razon: string | null;
  } | null;
  periods: string[];
  has_xml: boolean;
  presentation?: {
    mode: 'ORIGINAL_PDF' | 'XML_RENDER' | 'METADATA_RENDER' | 'UNAVAILABLE';
    missing: string[];
  };
}

/** Un decimal del XML a céntimos, para poder decirlo en letras. */
function minorOf(raw: string | null | undefined): number | null {
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  return Number.isNaN(n) ? null : Math.round(n * 100);
}

/**
 * «₡73,450.00» a partir del texto plano del XML, que viene sin formato.
 *
 * Va por `formatMoney` y no por `toLocaleString('es-CR')`: el `Intl` del
 * navegador agrupa los miles con espacio fino, que no es como se escribe el
 * monto ni en el resto de la app ni en el comprobante original.
 */
function colones(raw: string | null | undefined): string {
  const minor = minorOf(raw);
  if (minor == null) return raw == null || raw === '' ? '—' : String(raw);
  /* Con un espacio entre el símbolo y la cifra, que es como los separa el
     original —«¢ 65,000.00»—. En el resto de la app van pegados (`formatMoney`),
     y está bien: acá la regla es la del papel, no la de la aplicación.
     El símbolo sí es el nuestro: GTI imprime «¢», el signo de centavo, y el
     del colón es «₡». Copiar un error de tipografía no es ser fiel. */
  return formatMoney(minor, { cents: true }).replace('₡', '₡ ');
}

const positive = (raw: string | null | undefined): boolean => Number(raw ?? 0) > 0;

/** El mismo importe sin el símbolo: en el detalle el original imprime la cifra
 *  desnuda —«65,000.00»— y guarda el símbolo para los totales. */
function cifra(raw: string | null | undefined): string {
  return colones(raw).replace(/[^\d.,-]/g, '');
}

/**
 * Cómo NOMBRA el comprobante lo que el catálogo de Hacienda numera.
 *
 * El catálogo de `@cleanblue/shared` es el de Hacienda y lo usa toda la app;
 * este mapa es solo para el documento, porque acá la regla es otra: decir lo
 * que dice el papel. GTI abrevia «Cédula Jurídica» como «Ident. Jurídica» y
 * escribe el medio de pago con otra puntuación. Lo que no esté acá cae al
 * catálogo, que es lo correcto para cualquier código que GTI todavía no nos
 * haya mostrado impreso.
 */
const ETIQUETA_IMPRESA: Record<string, string> = {
  'ident:01': 'Ident. Física',
  'ident:02': 'Ident. Jurídica',
  'ident:03': 'Ident. DIMEX',
  'ident:04': 'Ident. NITE',
  'medio:04': 'Transferencia-Depósito Bancario',
  /* El catálogo de Hacienda escribe «SINPE MOVIL» sin tilde —así está en el
     anexo de la 4.4, y así se queda en `TIPO_MEDIO_PAGO`, que es el catálogo—
     pero el papel lo imprime acentuado. Acá manda el papel. */
  'medio:06': 'SINPE MÓVIL',
};

const comoLoImprime = (
  prefijo: string, codigo: string | null | undefined, catalogo: Record<string, string>,
): string | null => (
  (codigo != null ? ETIQUETA_IMPRESA[`${prefijo}:${codigo}`] : null) ?? haciendaLabel(catalogo, codigo)
);

/** `506` + `83095765` → `«+(506) 8309-5765»`, como lo imprime el comprobante. */
function telefonoLabel(country: string | null | undefined, num: string | null | undefined): string | null {
  if (num == null || num === '') return null;
  const n = num.trim();
  const cuerpo = /^\d{8}$/.test(n) ? `${n.slice(0, 4)}-${n.slice(4)}` : n;
  return country ? `+(${country}) ${cuerpo}` : cuerpo;
}

/**
 * El pie legal, palabra por palabra como lo imprime el comprobante. Es texto
 * fijo de la resolución vigente, no un dato del XML.
 */
const PIE_LEGAL = 'Autorizado mediante la resolución MH-DGT-RES-0027-2024 del 13 de noviembre de 2024 '
  + 'de la Dirección General de Tributación, publicado en el diario oficial la Gaceta el 19 de '
  + 'noviembre de 2024.';

/**
 * Los proveedores de facturación que sabemos nombrar, por su cédula jurídica en
 * `<ProveedorSistemas>`. Uno que no esté acá se muestra por su cédula: el XML
 * guarda el número, no el nombre comercial.
 */
const PROVEEDOR_SISTEMAS: Record<string, string> = {
  '3101225890': 'GTI, www.facturaelectronica.cr',
};

/**
 * Lo que codifica el QR del comprobante: el portal de consulta más la clave.
 *
 * Se verificó decodificando el QR del PDF original de GTI, no se dedujo. Como
 * la clave está en el XML, el QR sale idéntico módulo a módulo — misma versión
 * (6), mismo nivel de corrección (M), misma máscara.
 */
const QR_PORTAL = 'https://www.facturaelectronica.co.cr/QR/';

/**
 * Cómo se llama el gran total según el documento. Una nota de crédito no dice
 * «Total Factura»: dice lo que es, y esa distinción es la que sostiene el
 * tratamiento aparte que las notas de crédito ya tienen en esta pantalla.
 */
const TOTAL_ETIQUETA: Record<string, string> = {
  FacturaElectronica: 'Total Factura:',
  NotaCreditoElectronica: 'Total Nota de Crédito:',
  NotaDebitoElectronica: 'Total Nota de Débito:',
  TiqueteElectronico: 'Total Tiquete:',
};
/**
 * El comprobante, pintado como el documento que emite GTI.
 *
 * ── El criterio ────────────────────────────────────────────────────────────
 *
 * «Deben ser idénticas. A excepción de las variaciones de los datos», pidió el
 * dueño. No es una vista bonita del mismo dato: es el mismo papel. Un cliente
 * que recibió el PDF en marzo y abre el sistema en agosto tiene que ver lo
 * mismo, y una factura vieja y una nueva tienen que ser el mismo documento.
 *
 * Por eso el orden de los bloques, los filetes, los anchos de columna y los
 * cuerpos de letra salen de medir el PDF original, no de estimarlos. El detalle
 * de las medidas está en `invoice-doc.css`.
 *
 * ── Lo que el original imprime y acá NO aparece ────────────────────────────
 *
 * Tres cosas, y las tres por el mismo motivo: **no están en el XML**, que es la
 * única fuente que tenemos. Inventarlas sería peor que omitirlas.
 *
 *   · «Código Único de Consulta» — lo genera el portal del proveedor. No es
 *     derivable de la clave: en dos facturas reales tiene largos distintos
 *     (8 y 15 caracteres).
 *   · «Destinatario» — un nombre de contacto que GTI guarda en su sistema; el
 *     `<Receptor>` del comprobante no lo trae.
 *   · «Fax» — GTI imprime la etiqueta con el código de país y nada más. Copiar
 *     un campo vacío sería copiar un defecto.
 *
 * ── Y una línea que el original no trae ────────────────────────────────────
 *
 * La aclaración del pie, por pedido expreso (P2.4): esta vista también la ve el
 * cliente, así que el documento dice qué es. Es la única excepción deliberada a
 * la fidelidad campo por campo, y existe justamente para que la copia no se
 * confunda con el original.
 */
/**
 * El comprobante a «ajustar al ancho», como un visor de PDF.
 *
 * El documento es fiel al original y por eso tiene ancho FIJO (A4: 559 pt ≈
 * 745 px): en un teléfono no se reacomoda —es el mismo papel— sino que se
 * escala completo, igual que hace cualquier visor con un PDF. Sin esto, el
 * modal de la factura se salía de los márgenes en el celular (reporte del
 * dueño, 27-ago-2026): 641 px de documento sobre 375 de pantalla.
 *
 * La impresión NO se escala: `invoice-doc.css` anula la transformación en
 * `@media print`, porque el papel sí tiene los 559 pt.
 *
 * Entra entera SIEMPRE, en toda superficie: teléfono, escritorio y la app
 * instalada. Hubo un tiempo un botón «Ampliar» que apagaba la escala para poder
 * leer el papel de verdad en un teléfono; el dueño lo dio de baja el 8-set-2026
 * porque era un modo que había que descubrir y elegir. Quien necesite leerlo de
 * cerca tiene el zoom del sistema, y para llevárselo está «Imprimir o guardar
 * PDF», que sale del tamaño real y no de lo que se ve en pantalla.
 */
export function DocumentoEscalado({ children }: { children: React.ReactNode }) {
  const marco = useRef<HTMLDivElement>(null);
  const hoja = useRef<HTMLDivElement>(null);
  const [ajuste, setAjuste] = useState<{ escala: number; alto: number } | null>(null);

  useLayoutEffect(() => {
    const m = marco.current, h = hoja.current;
    if (!m || !h) return;
    const medir = () => {
      // scrollWidth y no offsetWidth: la escala anterior no debe contaminar la
      // medida del ancho natural (la hoja se mide sin transformar, abajo).
      const natural = h.scrollWidth;
      const escala = natural > 0 ? Math.min(1, m.clientWidth / natural) : 1;
      setAjuste({ escala, alto: h.scrollHeight * escala });
    };
    medir();
    const ro = new ResizeObserver(medir);
    ro.observe(m);
    ro.observe(h);
    return () => ro.disconnect();
  }, []);

  return (
    <div ref={marco} className="doc-visor" style={ajuste ? { height: ajuste.alto } : undefined}>
      <div
        ref={hoja}
        className="doc-visor__hoja"
        style={ajuste && ajuste.escala < 1 ? { transform: `scale(${ajuste.escala})` } : undefined}
      >
        {children}
      </div>
    </div>
  );
}

/** El mismo visor para administrador y cliente, con la prioridad PDF → XML → metadata. */
export function InvoiceViewer({ documentType, receptorName, d, pdfUrl }: {
  documentType: string | null;
  receptorName: string | null;
  d: InvoiceDetail;
  pdfUrl: string;
}) {
  if (d.presentation?.mode === 'ORIGINAL_PDF') {
    return (
      <div className="invoice-pdf-viewer">
        <iframe
          title={`Factura ${d.document_number}`}
          src={`${pdfUrl}#view=FitH`}
          className="invoice-pdf-viewer__frame"
        />
      </div>
    );
  }

  if (d.presentation?.mode === 'UNAVAILABLE') {
    return (
      <div className="invoice-unavailable">
        <strong>La vista todavía no está disponible.</strong>
        <p>Falta completar: {d.presentation.missing.join(', ')}.</p>
      </div>
    );
  }

  return (
    <>
      {d.presentation?.mode === 'METADATA_RENDER' && (
        <div className="invoice-generated-note">
          Vista generada desde los datos registrados. No sustituye el XML fiscal.
        </div>
      )}
      <DocumentoEscalado>
        <InvoiceDocument documentType={documentType} receptorName={receptorName} d={d} />
      </DocumentoEscalado>
    </>
  );
}

export function InvoiceDocument({ documentType, receptorName, d }: {
  /** Lo unico que el documento necesita del listado: el tipo (para el titulo)
   *  y el nombre del receptor como respaldo si el XML no lo trajera. Se piden
   *  sueltos y no como fila entera para que el portal del cliente pueda
   *  reutilizar este mismo render sin fabricar una fila de administrador. */
  documentType: string | null; receptorName: string | null; d: InvoiceDetail;
}) {
  const emisor = d.emisor;
  const receptor = d.receptor;
  const titulo = (d.tipo_comprobante && TIPO_COMPROBANTE[d.tipo_comprobante])
    ?? (isCreditNote(documentType) ? 'Nota de Crédito Electrónica' : 'Factura Electrónica');
  const totalMinor = minorOf(d.totals?.comprobante);
  const qr = d.clave ? qrSvg(QR_PORTAL + d.clave) : null;
  const proveedor = d.proveedor_sistemas
    ? (PROVEEDOR_SISTEMAS[d.proveedor_sistemas] ?? `proveedor ${d.proveedor_sistemas}`)
    : null;
  const telEmisor = telefonoLabel(emisor?.phone_country, emisor?.phone);
  const telReceptor = telefonoLabel(receptor?.phone_country, receptor?.phone);

  return (
    <div className="invoice-doc">
      <header className="invoice-doc__top">
        <img className="invoice-doc__logo" src="/logo-cleanblue.png" alt="Clean Blue" />
        <div className="invoice-doc__contacto">
          {emisor?.address && <div><b>Dirección:</b> {emisor.address}</div>}
          {telEmisor && <div><b>Teléfono:</b> {telEmisor}</div>}
          {emisor?.email && <div><b>Correo:</b> {emisor.email}</div>}
        </div>
      </header>

      <div className="invoice-doc__emisor">
        {emisor?.trade_name && <div className="invoice-doc__comercial">{emisor.trade_name}</div>}
        <div className="invoice-doc__razon">{emisor?.legal_name ?? 'CLEAN BLUE LTDA.'}</div>
        {emisor?.tax_id && (
          <div>
            <b>{comoLoImprime('ident', emisor.tax_id_type, TIPO_IDENTIFICACION) ?? 'Identificación'}:</b>{' '}
            {cedulaLabel(emisor.tax_id, emisor.tax_id_type)}
          </div>
        )}
      </div>

      <div className="invoice-doc__partes">
        <div>
          <div>Receptor {receptor?.legal_name ?? receptorName ?? '—'}</div>
          {receptor?.tax_id && (
            <div>
              <b>{comoLoImprime('ident', receptor.tax_id_type, TIPO_IDENTIFICACION) ?? 'Identificación'}:</b>{' '}
              {cedulaLabel(receptor.tax_id, receptor.tax_id_type)}
            </div>
          )}
          {telReceptor && <div><b>Teléfono:</b> {telReceptor}</div>}
          {receptor?.email && <div><b>Correo:</b> {receptor.email}</div>}
          {/* El comprobante lo llama «Código Interno»: es el código del cliente
              en el sistema del proveedor, que es el mismo `unit_code` de acá. */}
          {d.unit_code && <div><b>Código Interno:</b> {d.unit_code}</div>}
          {receptor?.address && (
            <div className="invoice-doc__espaciado"><b>Dirección:</b> {receptor.address}</div>
          )}
        </div>

        <div>
          <div><b>{titulo} N°</b> {d.document_number}</div>
          {d.clave && (
            <>
              {/* La clave numérica es lo que identifica el comprobante ante
                  Hacienda: es lo que un contador busca, y lo que codifica el QR. */}
              <div><b>Clave Numérica:</b></div>
              <div className="invoice-doc__clave-num">{d.clave}</div>
            </>
          )}
          <div className="invoice-doc__espaciado">
            <b>Fecha de Emisión:</b> {fechaEmisionLabel(d.issued_on)}
          </div>
          {d.condicion_venta && (
            <div className="invoice-doc__espaciado">
              <b>Condición Venta:</b> {haciendaLabel(CONDICION_VENTA, d.condicion_venta)}
            </div>
          )}
          {/* Lo que hace que una nota de crédito sea una nota de crédito. El
              original de una factura no lo trae porque una factura no
              referencia a ningún otro comprobante. */}
          {d.referencia && (
            <div className="invoice-doc__espaciado">
              <div>
                <b>
                  {d.referencia.codigo === '01'
                    ? 'Anula el comprobante N°'
                    : 'Documento de referencia N°'}
                </b>{' '}
                {d.referencia.consecutivo ?? d.referencia.clave}
              </div>
              {d.referencia.razon && <div><b>Razón:</b> {d.referencia.razon}</div>}
            </div>
          )}
        </div>
      </div>

      {d.medios_pago?.map((mp, i) => (
        <div className="invoice-doc__medio" key={i}>
          <b>Medio de Pago:</b> {comoLoImprime('medio', mp.tipo, TIPO_MEDIO_PAGO)}
        </div>
      ))}

      <table className="invoice-doc__tabla">
        <colgroup>
          <col /><col /><col /><col /><col /><col /><col /><col /><col />
        </colgroup>
        <thead>
          <tr>
            <th>Código / Cód. CABYS</th>
            <th>Cantidad</th>
            <th>Unidad Medida</th>
            <th>Descripción del Producto/Servicio</th>
            <th>Precio Unitario</th>
            <th>Descuento</th>
            <th>Naturaleza del Desc.</th>
            <th className="num">Subtotal</th>
            <th className="num">Monto Impuestos</th>
          </tr>
        </thead>
        <tbody>
          {d.lines.map((l, i) => (
            <tr key={i}>
              <td>
                {l.codigo_comercial && (
                  <div title={haciendaLabel(TIPO_CODIGO_COMERCIAL, l.codigo_comercial_tipo) ?? undefined}>
                    {l.codigo_comercial}
                  </div>
                )}
                {l.cabys && <div className="invoice-doc__cabys">{l.cabys}</div>}
              </td>
              <td className="num">{l.cantidad ?? ''}</td>
              <td>{l.unidad ?? ''}</td>
              <td>{l.detalle}</td>
              <td className="num">{cifra(l.precio_unitario)}</td>
              <td className="num">{positive(l.descuento) ? cifra(l.descuento) : '0.00'}</td>
              <td>{l.naturaleza_descuento ?? ''}</td>
              <td className="num">{cifra(l.subtotal)}</td>
              <td className="num">{cifra(l.impuesto)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="invoice-doc__cierre">
        <div className="invoice-doc__notas">
          {/* «Notas:» siempre, como el original, y sin interpretar el contenido.
              Una nota de crédito mete acá la razón de la anulación y a veces
              también el mes —«logo incorrecto · Esta factura corresponde al mes
              de Octubre»—, así que un rótulo único mentiría sobre la mitad. La
              razón, cuando importa, ya sale arriba junto al comprobante que
              anula, que es donde el documento la declara. */}
          {(d.notas?.length ?? 0) > 0 && (
            <div><b>Notas:</b> {d.notas!.join(' · ')}</div>
          )}
        </div>

        {d.totals && (
          <div className="invoice-doc__totales">
            <div><span>Subtotal Neto</span><span>{colones(d.totals.venta_neta)}</span></div>
            {positive(d.totals.descuentos) && (
              <div><span>Total Descuentos</span><span>{colones(d.totals.descuentos)}</span></div>
            )}
            {positive(d.totals.exento) && (
              <div><span>Total Exento</span><span>{colones(d.totals.exento)}</span></div>
            )}
            {positive(d.totals.exonerado) && (
              <div><span>Total Exonerado</span><span>{colones(d.totals.exonerado)}</span></div>
            )}
            <div><span>Total Impuesto</span><span>{colones(d.totals.impuesto)}</span></div>
            <div className="invoice-doc__total">
              <span>{TOTAL_ETIQUETA[d.tipo_comprobante ?? ''] ?? 'Total Comprobante:'}</span>
              <span>{colones(d.totals.comprobante)}</span>
            </div>
            {totalMinor != null && (
              <div className="invoice-doc__letras">{montoEnLetras(totalMinor, d.totals.moneda)}</div>
            )}
            {d.totals.tipo_cambio && d.totals.moneda !== 'CRC' && (
              <div className="invoice-doc__letras">Tipo de cambio: {d.totals.tipo_cambio}</div>
            )}
          </div>
        )}
      </div>

      <div className="invoice-doc__consulta">
        Consulta en <b>www.facturaelectronica.cr</b>
      </div>

      <footer className="invoice-doc__pie">
        <div className="invoice-doc__legal">
          <p>{PIE_LEGAL}</p>
          {proveedor && <p>Generada por {proveedor}</p>}
          {d.version && <p>Versión del Documento Electrónico: {d.version}</p>}
          {d.firmado && <p>Comprobante firmado digitalmente.</p>}
          <p className="invoice-doc__aclaracion">
            Esta vista es una representación del comprobante electrónico; el
            comprobante legal ante Hacienda es el XML firmado.
          </p>
        </div>
        {qr && (
          // Generado por código desde la clave del XML: mismo contenido que el
          // QR del comprobante original, verificado módulo a módulo.
          <div className="invoice-doc__qr" dangerouslySetInnerHTML={{ __html: qr }} />
        )}
      </footer>

      <div className="invoice-doc__pagina">Página 1 de 1</div>

      {!d.has_xml && (
        <p className="invoice-doc__sin-archivo">
          El archivo del comprobante no está cargado; se muestra lo que hay registrado.
        </p>
      )}
    </div>
  );
}
